import { useState, useEffect } from 'react';
import { COMPANY_INFO, PROJECTS } from '../data/staticData';
import { getProjects } from '../lib/cmsClient';
import ContactForm from '../components/ContactForm';

export default function ProjectsPage() {
  const [projects, setProjects] = useState(PROJECTS);
  const [activeCategory, setActiveCategory] = useState('Alle');
  const [visibleCount, setVisibleCount] = useState(6);
  const [selectedProject, setSelectedProject] = useState(null);
  const [activeModalImage, setActiveModalImage] = useState(null);

  // Fetch latest projects from CMS
  useEffect(() => {
    async function load() {
      const data = await getProjects();
      if (data && data.length > 0) {
        setProjects(data);
      }
    }
    load();
  }, []);

  // Close modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setSelectedProject(null);
      }
    };
    if (selectedProject) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedProject]);

  // Set active modal image when selected project changes
  useEffect(() => {
    if (selectedProject) {
      setActiveModalImage(selectedProject.image);
    } else {
      setActiveModalImage(null);
    }
  }, [selectedProject]);

  const categories = ['Alle', 'Tag', 'Energiløsninger'];

  const filteredProjects = activeCategory === 'Alle'
    ? projects
    : projects.filter((p) => (p.category || 'Tag') === activeCategory);

  const displayedProjects = filteredProjects.slice(0, visibleCount);
  const hasMore = visibleCount < filteredProjects.length;

  const handleOpenProject = (project) => {
    setSelectedProject(project);
    setActiveModalImage(project.image || (project.gallery_images && project.gallery_images[0]) || null);
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 6);
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-14 lg:pt-16 pb-8 sm:pb-12">
        <div className="max-w-4xl space-y-5">
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold text-neutral-900 tracking-tight leading-tight">
            Se nogle af vores tidligere projekter
          </h1>
          <p className="text-neutral-600 text-base sm:text-lg leading-relaxed max-w-3xl">
            Vi viser stolt vores tidligere projekter frem, så du kan se nogle af de mange løsninger, vi har udført for vores kunder. Har du spørgsmål? Kontakt os endelig, så finder vi sammen frem til den bedste løsning til dig og din bolig.
          </p>

          {/* Category Filter Pills */}
          <div className="flex flex-wrap items-center gap-2 pt-2">
            {categories.map((cat) => {
              const isActive = activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => {
                    setActiveCategory(cat);
                    setVisibleCount(6);
                  }}
                  className={`px-5 py-2 rounded-full font-medium text-sm transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-neutral-900 text-white shadow-sm'
                      : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Projects Showcase Grid (2-kolonners layout svarende til dansktagbyg.dk) */}
      <section className="pb-16 sm:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {displayedProjects.map((project) => {
              const gallery = project.gallery_images && project.gallery_images.length > 0
                ? project.gallery_images
                : [project.image];

              return (
                <div
                  key={project.id}
                  className="group bg-white rounded-none sm:rounded-sm overflow-hidden flex flex-col justify-between transition-all duration-300"
                >
                  {/* Billede med pæne proportioner og hover-zoom */}
                  <div
                    className="relative aspect-[16/10] sm:aspect-[16/9.5] w-full bg-neutral-100 overflow-hidden cursor-pointer"
                    onClick={() => handleOpenProject(project)}
                  >
                    <img
                      src={project.image}
                      alt={project.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-500"
                    />
                    {gallery.length > 1 && (
                      <span className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 pointer-events-none">
                        <i className="fa-regular fa-images"></i>
                        <span>{gallery.length} billeder</span>
                      </span>
                    )}
                  </div>

                  {/* Tekst & metadata */}
                  <div className="pt-5 pb-2 flex-grow space-y-2.5">
                    {/* Kategori pille-badge */}
                    <div>
                      <span className="inline-block bg-neutral-800 text-white text-[11px] font-semibold uppercase px-3 py-1 rounded-full tracking-wider">
                        {project.category || 'Tag'}
                      </span>
                    </div>

                    {/* Titel */}
                    <h2
                      onClick={() => handleOpenProject(project)}
                      className="text-xl sm:text-2xl font-bold text-neutral-900 group-hover:text-[#BE0F12] transition-colors cursor-pointer leading-snug"
                    >
                      {project.title}
                    </h2>

                    {/* Specifikationer: M² pris, tagtype og lokation */}
                    <div className="text-xs sm:text-sm text-neutral-600 space-y-1">
                      {project.m2_price && (
                        <p className="flex items-center gap-1.5">
                          <strong className="text-neutral-900 font-semibold">M² pris:</strong>
                          <span>{project.m2_price}</span>
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-neutral-600">
                        {project.roof_type && (
                          <p className="flex items-center gap-1.5">
                            <strong className="text-neutral-900 font-semibold">Tagtype:</strong>
                            <span>{project.roof_type}</span>
                          </p>
                        )}
                        {project.location && (
                          <p className="flex items-center gap-1.5">
                            <strong className="text-neutral-900 font-semibold">Lokation:</strong>
                            <span>{project.location}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Se hele projektet knap / tekstlink */}
                  <div className="pt-2">
                    <button
                      onClick={() => handleOpenProject(project)}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-neutral-900 hover:text-[#BE0F12] group-hover:text-[#BE0F12] transition-colors cursor-pointer py-1"
                    >
                      <span>Se hele projektet</span>
                      <i className="fa-solid fa-arrow-right text-xs transition-transform group-hover:translate-x-1 text-[#BE0F12]"></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Indlæs flere knap hvis der er flere projekter */}
          {hasMore && (
            <div className="pt-14 text-center">
              <button
                onClick={handleLoadMore}
                className="inline-flex items-center justify-center px-9 py-3 bg-neutral-800 hover:bg-neutral-900 text-white font-medium text-sm rounded-full transition-colors shadow-sm hover:shadow cursor-pointer"
              >
                Indlæs flere
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Modal for fuld visning af projektets detaljer */}
      {selectedProject && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
          onClick={() => setSelectedProject(null)}
        >
          <div
            className="bg-white max-w-3xl w-full rounded-xl sm:rounded-2xl p-6 sm:p-8 space-y-6 max-h-[92vh] overflow-y-auto shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex justify-between items-start gap-4 pb-2 border-b border-neutral-100">
              <div className="space-y-1">
                <span className="inline-block bg-neutral-800 text-white text-xs font-semibold uppercase px-3 py-1 rounded-full tracking-wider">
                  {selectedProject.category || 'Tag'}
                </span>
                <h3 className="text-2xl sm:text-3xl font-bold text-neutral-900 leading-tight">
                  {selectedProject.title}
                </h3>
              </div>
              <button
                onClick={() => setSelectedProject(null)}
                aria-label="Luk modal"
                className="w-9 h-9 rounded-full bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-neutral-900 flex items-center justify-center transition-colors cursor-pointer text-lg font-bold shrink-0"
              >
                ✕
              </button>
            </div>

            {/* Stort billede */}
            <div className="space-y-3">
              <div className="aspect-[16/10] w-full bg-neutral-100 rounded-lg overflow-hidden border border-neutral-200">
                <img
                  src={activeModalImage || selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Galleri miniature-billeder hvis der er flere */}
              {selectedProject.gallery_images && selectedProject.gallery_images.length > 1 && (
                <div className="flex items-center gap-2 overflow-x-auto pb-1">
                  {selectedProject.gallery_images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveModalImage(img)}
                      className={`relative w-20 h-14 rounded overflow-hidden border-2 transition-all shrink-0 cursor-pointer ${
                        activeModalImage === img ? 'border-[#BE0F12] ring-2 ring-[#BE0F12]/30' : 'border-neutral-200 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`${selectedProject.title} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Nøgledata / specifikationer */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 bg-neutral-50 rounded-lg border border-neutral-200 text-sm">
              {selectedProject.m2_price && (
                <div>
                  <span className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider">M² pris</span>
                  <span className="font-bold text-neutral-900">{selectedProject.m2_price}</span>
                </div>
              )}
              {selectedProject.roof_type && (
                <div>
                  <span className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider">Tagtype</span>
                  <span className="font-bold text-neutral-900">{selectedProject.roof_type}</span>
                </div>
              )}
              {selectedProject.location && (
                <div>
                  <span className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider">Lokation</span>
                  <span className="font-bold text-neutral-900">{selectedProject.location}</span>
                </div>
              )}
            </div>

            {/* Beskrivelse og punktopstilling */}
            <div className="space-y-4 text-neutral-700 text-sm sm:text-base leading-relaxed">
              {selectedProject.content && selectedProject.content.includes('<') ? (
                <div 
                  className="prose prose-sm max-w-none text-neutral-700 space-y-2 [&>ul]:list-disc [&>ul]:pl-5 [&>p]:mb-2"
                  dangerouslySetInnerHTML={{ __html: selectedProject.content }}
                />
              ) : (
                <p>{selectedProject.content || selectedProject.description}</p>
              )}

              {selectedProject.included && selectedProject.included.length > 0 && (
                <div className="pt-2">
                  <h4 className="font-bold text-neutral-900 text-sm uppercase tracking-wide mb-3">
                    Projektet inkluderede:
                  </h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {selectedProject.included.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2.5">
                        <i className="fa-solid fa-check text-[#BE0F12] text-xs shrink-0"></i>
                        <span className="text-neutral-800">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Modal handlinger */}
            <div className="pt-4 border-t border-neutral-200 flex flex-col sm:flex-row justify-between items-center gap-3">
              <a
                href="#kontakt"
                onClick={() => setSelectedProject(null)}
                className="w-full sm:w-auto px-6 py-2.5 bg-[#BE0F12] hover:bg-red-800 text-white font-medium text-xs uppercase tracking-wider rounded-full transition-colors text-center cursor-pointer"
              >
                Få tilbud på lignende opgave
              </a>
              <button
                onClick={() => setSelectedProject(null)}
                className="w-full sm:w-auto px-6 py-2.5 bg-neutral-200 hover:bg-neutral-300 text-neutral-800 font-medium text-xs uppercase tracking-wider rounded-full transition-colors cursor-pointer"
              >
                Luk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-footer Call-to-action: "Kontakt os og få den rette rådgivning" */}
      <section className="py-16 sm:py-20 bg-white text-center border-t border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 space-y-5">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-neutral-900 tracking-tight">
            Kontakt os og få den rette rådgivning
          </h2>
          <div>
            <a
              href="#kontakt"
              className="inline-block px-8 py-3.5 bg-[#BE0F12] hover:bg-red-800 text-white font-medium text-sm rounded-full tracking-normal transition-colors"
            >
              Kontakt os
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof Rating Banner (4,8 ud af 155 anmeldelser på mørk baggrund som originalen) */}
      <section className="bg-neutral-800 py-3.5 text-center text-white">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-center gap-3">
          <div className="flex items-center gap-1 text-amber-400 text-sm">
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star"></i>
            <i className="fa-solid fa-star-half-stroke"></i>
          </div>
          <a
            href="https://www.anmeld-haandvaerker.dk/tagdaekker/dansk-tagbyg-aps"
            target="_blank"
            rel="noreferrer"
            className="font-bold text-sm sm:text-base text-white hover:text-stone-300 transition-colors"
          >
            4,8 ud af 155 anmeldelser
          </a>
        </div>
      </section>

      {/* Omfattende bund: Firmaoplysninger, Danmarkskort, Dækningsområde og Kontaktformular */}
      <section id="kontakt" className="py-16 sm:py-20 bg-neutral-100 scroll-mt-24 border-t border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* Venstre kolonne: Firmaoplysninger, dækningskort og byer */}
            <div className="lg:col-span-6 space-y-8">
              {/* Firma & Kontaktkort */}
              <div className="p-6 sm:p-8 bg-white border border-neutral-200 rounded-sm shadow-sm space-y-4">
                <h3 className="font-extrabold text-xl sm:text-2xl text-neutral-900">
                  Dansk Tagbyg ApS
                </h3>
                <div className="space-y-2.5 text-sm text-neutral-700">
                  <p>
                    <strong className="text-neutral-900">Ring:</strong>{' '}
                    <a href={`tel:${COMPANY_INFO.phone}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.phoneFormatted}
                    </a>
                  </p>
                  <p>
                    <strong className="text-neutral-900">Skriv:</strong>{' '}
                    <a href={`mailto:${COMPANY_INFO.email}`} className="text-[#BE0F12] font-bold hover:underline">
                      {COMPANY_INFO.email}
                    </a>
                  </p>
                  <p>
                    <strong className="text-neutral-900">Adr:</strong> {COMPANY_INFO.address}
                  </p>
                  <p>
                    <strong className="text-neutral-900">CVR:</strong> {COMPANY_INFO.cvr}
                  </p>
                </div>
              </div>

              {/* Vi løser opgaver i hele DK & By-oversigt */}
              <div className="p-6 sm:p-8 bg-white border border-neutral-200 rounded-sm shadow-sm space-y-6">
                <div>
                  <h4 className="font-bold text-lg text-neutral-900 mb-2">
                    Vi løser opgaver i hele DK
                  </h4>
                  <div className="w-full flex justify-center py-2">
                    <img
                      src="/_assets/denmark-map_b02da195.svg"
                      alt="Kort over Danmark med opgaver"
                      className="h-36 sm:h-44 w-auto object-contain opacity-85"
                    />
                  </div>
                </div>

                <div className="pt-4 border-t border-neutral-100">
                  <h5 className="font-bold text-sm text-neutral-900 mb-3">
                    Nyt tag i dit område
                  </h5>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs sm:text-sm text-neutral-600">
                    <span>Aarhus</span>
                    <span>Odder</span>
                    <span>Randers</span>
                    <span>Hinnerup</span>
                    <span>Horsens</span>
                    <span>Højbjerg</span>
                    <span>Skanderborg</span>
                    <span>Risskov</span>
                    <span>Djursland</span>
                    <span>Hammel</span>
                  </div>
                </div>

                {/* Branchemedlemskaber */}
                <div className="pt-4 border-t border-neutral-100">
                  <span className="block text-xs font-semibold text-neutral-500 uppercase tracking-wider mb-2.5">
                    Vi er medlem af
                  </span>
                  <div className="flex flex-wrap items-center gap-3 text-xs font-semibold text-neutral-700">
                    <span className="px-3 py-1.5 bg-neutral-100 border border-neutral-200 rounded">Byg Garanti</span>
                    <span className="px-3 py-1.5 bg-neutral-100 border border-neutral-200 rounded">DI Byggeri</span>
                    <span className="px-3 py-1.5 bg-neutral-100 border border-neutral-200 rounded">Swisspearl</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Højre kolonne: Skriv til os formular */}
            <div className="lg:col-span-6 bg-white border border-neutral-200 p-8 sm:p-10 rounded-sm shadow-sm">
              <ContactForm
                idPrefix="projects"
                title="Kontakt os"
                subtitle="Udfyld formularen nedenunder, så vender vi tilbage hurtigst muligt."
                submitButtonText="Send henvendelse"
              />
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}